import React, { useState, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Container, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle, faInfoCircle, faRotateRight, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { getErrorMessage } from "services/UtilityService";
import { sendAuthenticatedAsyncRequestWithResponseType } from "../../services/AsyncRequestService";
import Spinner from "../Spinner";
import { API_RESPONSE_TYPE } from "lookup/AsyncRequestConstants";
import "./ConfigurablePdfViewer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import { GlobalWorkerOptions } from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

GlobalWorkerOptions.workerSrc = pdfWorker;

// Generic info box renderer
const renderInfoBox = (type, message, icon = null) => {
  const typeConfig = {
    error: { icon: faExclamationCircle, iconColor: "#AA483E", messageColor: "#252F3C", backgroundColor: "#efd3cf" },
    info: { icon: faInfoCircle, iconColor: "#007BBB", messageColor: "#252F3C", backgroundColor: "#DDF4FF" },
    warning: { icon: faExclamationCircle, iconColor: "#FFAA3B", messageColor: "#252F3C", backgroundColor: "#f1d9b8" },
  };
  const config = typeConfig[type] || typeConfig.info;
  const displayIcon = icon || <FontAwesomeIcon icon={config.icon} />;

  if (type === "loading") {
    return (
      <Container fluid className="d-flex align-items-center justify-content-center info-box-container">
        <Card className="shadow-sm border info-box-card">
          <Card.Body className="text-center p-4">
            <Spinner />
            <p className="mt-3 mb-0 loading-message">
              {message || "Loading PDF..."}
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="d-flex align-items-center justify-content-center info-box-container">
      <Card className={`shadow-sm text-center info-box-card info-box-card-${type}`}>
        <Card.Body className="p-4">
          <div className="d-flex flex-column align-items-center">
            <div className={`info-box-icon info-box-icon-${type}`}>{displayIcon}</div>
            <p className="mb-0 info-box-message">
              {message}
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

const renderToolbar = (toolbarItems = []) => {
  if (!toolbarItems || toolbarItems.length === 0) {
    return null;
  }

  return (
    <div className="pdf-toolbar mb-2 d-flex justify-content-start gap-2">
      {toolbarItems.map((item, index) => {
        const {
          icon,
          onClick,
          label,
          variant = "outline-primary",
          size = "sm",
          disabled = false,
          className = "",
          ...buttonProps
        } = item;

        return (
          <Button
            key={index}
            size={size}
            variant={variant}
            onClick={onClick}
            disabled={disabled}
            className={className}
            {...buttonProps}
          >
            {icon && <FontAwesomeIcon icon={icon} />}
            {label && <span className={icon ? "ms-2" : ""}>{label}</span>}
          </Button>
        );
      })}
    </div>
  );
};

const ConfigurablePdfViewer = ({
  url,
  pdfHeight,
  pdfWidth,
  height,
  width,
  maxHeight,
  maxWidth,
  hiddenPages = [],
  showPageNumber = false,
}) => {
  const [pdfFile, setPdfFile] = useState(null); // Will hold ArrayBuffer
  const [numPages, setNumPages] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1.25);
  const [rotation, setRotation] = useState(0);

  const toolbarItems = [
    {
      icon: faRotateRight,
      onClick: () => setRotation((prev) => (prev + 90) % 360),
      title: "Rotate Clockwise",
    },
    {
      icon: faRotateLeft,
      onClick: () => setRotation((prev) => (prev - 90 + 360) % 360),
      title: "Rotate Counter-Clockwise",
    },
  ];

  useEffect(() => {
    if (url) fetchPdf(url);
  }, [url]);

  const fetchPdf = (pdfUrl) => {
    setLoading(true);
    setPdfFile(null);
    setErrorMsg(null);

    sendAuthenticatedAsyncRequestWithResponseType(
      pdfUrl,
      "GET",
      null,
      async (response) => {
        try {
          // Create a copy of the ArrayBuffer to avoid detached buffer issues
          // This ensures the buffer remains valid when passed to react-pdf
          const arrayBuffer = response.data;
          if (arrayBuffer instanceof ArrayBuffer) {
            // Create a new ArrayBuffer copy to avoid detachment issues
            const bufferCopy = arrayBuffer.slice(0);
            setPdfFile(bufferCopy);
          } else {
            setPdfFile(arrayBuffer);
          }
          setLoading(false);
          setRotation(0);
          setScale(1.25);
        } catch (err) {
          console.error("Failed to process PDF:", err);
          setErrorMsg(getErrorMessage("ERROR_FETCHING_PDF"));
          setLoading(false);
        }
      },
      (response) => {
        new Response(response.data)
          .json()
          .then((res) => setErrorMsg(res ? getErrorMessage(res) : getErrorMessage("ERROR_FETCHING_PDF")))
          .catch(() => setErrorMsg(getErrorMessage("ERROR_FETCHING_PDF")))
          .finally(() => setLoading(false));
      },
      API_RESPONSE_TYPE.ARRAY_BUFFER
    );
  };

  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);
  const onDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
    setErrorMsg(`Failed to load PDF: ${error.message || "Unknown error"}`);
    setLoading(false);
  };

  // Memoize the file object to prevent unnecessary reloads
  // Only recreate when pdfFile actually changes
  // IMPORTANT: This must be called before any conditional returns to follow Rules of Hooks
  const memoizedFile = useMemo(() => {
    if (!pdfFile) return null;
    return { data: pdfFile };
  }, [pdfFile]);

  const visiblePages = numPages
    ? Array.from({ length: numPages }, (_, i) => i + 1).filter((pageNum) => !hiddenPages.includes(pageNum))
    : [];

  if (loading) return renderInfoBox("loading", "Loading PDF...");
  if (errorMsg) return renderInfoBox("error", errorMsg);
  if (!pdfFile) return renderInfoBox("info", "No PDF file available.");
  if (numPages !== null && visiblePages.length === 0)
    return renderInfoBox("info", numPages === 0 ? "No pages available in this PDF." : "All pages are hidden.");

  // Container dimensions
  const containerHeight = height || pdfHeight || "100%";
  const containerWidth = width || pdfWidth || "100%";
  const containerMaxHeight = maxHeight || (height || pdfHeight ? undefined : "70vh");
  const containerMaxWidth = maxWidth || (width || pdfWidth ? undefined : "70vw");

  const containerStyle = {
    height: typeof containerHeight === "number" ? `${containerHeight}px` : containerHeight,
    width: typeof containerWidth === "number" ? `${containerWidth}px` : containerWidth,
    maxHeight: typeof containerMaxHeight === "number" ? `${containerMaxHeight}px` : containerMaxHeight,
    maxWidth: typeof containerMaxWidth === "number" ? `${containerMaxWidth}px` : containerMaxWidth,
  };

  return (
    <div className="w-full pdf-container" style={containerStyle}>
      <div className="pdf-scroll-content">
        {renderToolbar(toolbarItems)}
        <div className="pdf-pages-container">
          {memoizedFile && (
            <Document file={memoizedFile} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError} loading="">
              {visiblePages.map((pageNum, index) => (
                <div
                  key={`page_${pageNum}_${index}`}
                  className="pdf-page-wrapper"
                >
                  <div className="pdf-page-container">
                    <Page pageNumber={pageNum} scale={scale} rotate={rotation} />
                    {showPageNumber && (
                      <div className="pdf-page-number-badge">
                        Page {pageNum}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurablePdfViewer;
